import { FC, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { FaChevronLeft } from 'react-icons/fa'
import { useNavigate } from 'react-router-dom'

import { Box, Button, Grid } from '@mui/material'

import {
  useDELETE_meetingMutation,
  useGET_meetingByIdQuery,
  useGET_meetingCategoriesQuery,
  usePATCH_meetingStatusMutation,
  usePOST_meetingMutation,
  usePUT_meetingMutation,
} from 'redux/api/meetingsApi'
import { useGET_studentsProfileSelectQuery } from 'redux/api/studentsApi'
import { useGET_usersSelectQuery } from 'redux/api/usersApi'

import { Form, Formik } from 'formik'

import SubmitButton from 'components/atoms/buttons/SubmitButton'
import CheckboxField from 'components/atoms/fields/CheckboxField'
import DateTimePickerField from 'components/atoms/fields/DateTimePickerField'
import InputField from 'components/atoms/fields/InputField'
import RichTextField from 'components/atoms/fields/RichTextField'
import SelectField from 'components/atoms/fields/SelectField'
import ParticipantList from 'components/utilized/meetings/ParticipantList'

import useCrudData from 'utils/customHooks/useCrudData'
import formModel, { initialValues, validationSchema } from 'utils/formModels/meetingModel'
import { crudColorMap, crudIconMap, meetingsStatusSelectData, meetingsTypeSelectData } from 'utils/helpers'

import { ActionType, IParticipant } from 'types'
import { ScrollToError } from 'components/atoms/ScrollToError'

interface IProps {
  action: ActionType
  id: string
  viewOnly: boolean
  tabText?: string
  tabKey?: string
}

const MeetingForm: FC<IProps> = ({ action, id, viewOnly }) => {
  const { t } = useTranslation()
  const navigate = useNavigate()

  const backToSlug = '/meetings'

  const { isLoading, entity, handleSubmit, addResponseData, editResponseData } = useCrudData({
    backToSlug,
    entityName: `${t('meetings.meeting')}`,
    entityId: id,
    getEntityQuery: useGET_meetingByIdQuery,
    createEntityMutation: usePOST_meetingMutation,
    editEntityMutation: usePUT_meetingMutation,
    deleteEntityMutation: useDELETE_meetingMutation,
  })
  const [PATCH_meetingStatus, { isLoading: meetingStatusLoading, data: patchResponseData }] =
    usePATCH_meetingStatusMutation()

 const [disabled, setDisabled] = useState(true)

  useEffect(() => {
      const isDisabled = 
        action === 'edit' && (
        entity?.status !== 'pending' ||
        (patchResponseData && patchResponseData?.status !== 'pending') ||
        (addResponseData && addResponseData?.status !== 'pending') ||
        (editResponseData && editResponseData?.status !== 'pending')
      )
      setDisabled(isDisabled)
  }, [entity, patchResponseData, addResponseData, editResponseData])

  const {
    type,
    status,
    should_notify,
    student,
    facilitator,
    note_taker,
    location,
    purpose,
    meeting_url,
    agenda,
    start_datetime,
    duration_minutes,
    meeting_category,
    participants,
  } = formModel()

  const serverValues = entity?.id
    ? {
        ...entity,
        type: meetingsTypeSelectData().data.list.find((r) => r.id === entity?.type),
        status: meetingsStatusSelectData().data.list.find((r) => r.id === entity?.status),
        facilitator: { id: entity.facilitator_id || '', title: entity.facilitator || '' },
        note_taker: { id: entity.note_taker_id || '', title: entity.note_taker || '' },
        participants: entity?.participants.map((participant: IParticipant) => {
          return {
            participant: { id: participant.user_id, title: participant.title },
            role: participant.role,
            email: participant.email,
            phone: participant.phone,
            has_attended: participant.has_attended,
          }
        }),
      }
    : null


  return (
    <Formik
      enableReinitialize
      initialValues={serverValues || initialValues}
      validationSchema={validationSchema}
      onSubmit={(values: any) => {
        const participantsIds = values.participants.map((participant) => participant.participant?.id)
        const data = {
          type: values.type.id,
          status: values.status.id,
          should_notify: values.should_notify,
          student_id: values.student?.id,
          facilitator_id: values.facilitator.id,
          note_taker_id: values.note_taker.id,
          location: values.location,
          purpose: values.purpose,
          meeting_url: values.meeting_url,
          agenda: values.agenda,
          start_datetime: values.start_datetime,
          duration_minutes: Number(values.duration_minutes),
          meeting_category_id: values.meeting_category.id,
          participants_ids: participantsIds,
        }
        handleSubmit(action, id, data)
      }}
    >
      {({ values, setFieldValue, errors }) => {
        return (
          <Form>
            <ScrollToError/>
            <Grid container spacing={4} sx={{ marginTop: '-1rem' }}>
              <Grid item xs={12} md={3}>
                <SelectField
                  fieldProps={{ ...type }}
                  apiCall={() => meetingsTypeSelectData()}
                  disabled={viewOnly || disabled}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <SelectField
                  fieldProps={{ ...status }}
                  apiCall={() => meetingsStatusSelectData()}
                  disabled={viewOnly}
                  onChange={(value) => {
                    PATCH_meetingStatus({ id, body: { status: value.id } })
                  }}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <DateTimePickerField
                  fieldProps={{ ...start_datetime }}
                  disabled={viewOnly || disabled}
                  minDate={new Date()}
                />
              </Grid>
              <Grid item xs={12} md={3}>
                <InputField fieldProps={{ ...duration_minutes }} disabled={viewOnly || disabled} />
              </Grid>
              <Grid item xs={6} md={4}>
                <SelectField
                  fieldProps={{ ...meeting_category }}
                  apiCall={useGET_meetingCategoriesQuery}
                  disabled={viewOnly || disabled}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <InputField fieldProps={{ ...purpose }} disabled={viewOnly || disabled} />
              </Grid>
              <Grid item xs={6} md={4}>
                <InputField fieldProps={{ ...location }} disabled={viewOnly || disabled} />
              </Grid>
              <Grid item xs={6} md={4}>
                <SelectField
                  fieldProps={{ ...student }}
                  apiCall={useGET_studentsProfileSelectQuery}
                  disabled={viewOnly || disabled}
                />
              </Grid>
              <Grid item xs={6} md={4}>
                <InputField fieldProps={{ ...meeting_url }} disabled={viewOnly || disabled} />
              </Grid> 
              <Grid item xs={6} md={4} sx={{ display: 'flex' }}>
                 <CheckboxField fieldProps={{ ...should_notify }} disabled={viewOnly || disabled} />
              </Grid>
              <Grid item xs={12} lg={6}>
                <SelectField
                  fieldProps={{
                    ...facilitator,
                  }}
                  apiCall={useGET_usersSelectQuery}
                  disabled={viewOnly}
                />
              </Grid>
              <Grid item xs={12} lg={6}>
                <SelectField
                  fieldProps={{
                    ...note_taker,
                  }}
                  apiCall={useGET_usersSelectQuery}
                  disabled={viewOnly}
                />
              </Grid>
              <Grid item xs={12}>
                <RichTextField
                  fieldProps={{ ...agenda }}
                  label={agenda.label}
                  disabled={viewOnly || disabled}
                />
              </Grid>
              
              <Grid item xs={12}>
                <ParticipantList fieldProps={{ ...participants }} disabled={viewOnly || disabled} isProtocol={false}/>
              </Grid>
            </Grid>

            <Grid container spacing={4} sx={{ marginTop: '1rem' }}>
              <Grid item xs={12}>
                <Box sx={{ display: 'flex' }} className="transparent-background">
                  <Button
                    variant='outlined'
                    color='black'
                    onClick={() => navigate(backToSlug)}
                    sx={{ minHeight: '2.5rem', width: '10rem' }}
                  >
                    <FaChevronLeft style={{ fontSize: '1.1rem', margin: '-3px 0.5rem 0 -0.5rem' }} />
                    {`${t('common.back')}`}
                  </Button>
                  {action !== 'view' && (
                    <SubmitButton
                      color={crudColorMap[action]}
                      text={`${t('form-actions.' + action)}`}
                      icon={crudIconMap[action]}
                      sx={{ width: '10rem' }}
                      isLoading={isLoading || meetingStatusLoading}
                    />
                  )}
                </Box>
              </Grid>
            </Grid>
          </Form>
        )
      }}
    </Formik>
  )
}

export default MeetingForm
